// src/components/LeftNavigation.js
import React, { useState, useEffect } from "react";
import { getGlobalState, setGlobalState } from "../utils/globalState";
import {
  addUserDashboard,
  moveUserDashboard,
  deleteUserDashboard,
  deleteUserFolder,
  addUserFolder,
} from "../utils/api";
import MyTreeView from "./MyTreeView";
import AllTreeView from "./AllTreeView";
import themeConfig from "../config/themeConfig";
import "../styles/LeftNavigation.css";
import ConfirmModal from "./ConfirmModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDashboard } from "../context/DashboardContext";
import {
  faUserShield,
  faUserTie,
  faSignOutAlt,
  faHome,
  faBars,
  faFolder,
  faBullhorn,
  faMagnifyingGlass,
  faLightbulb,
  faLessThan,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import NewUserTour from "./NewUserTour";

const LeftNavigation = ({
  setCurrentPage,
  setSelectedNodeId,
  logout,
  currentTheme,
}) => {
  const [runTour, setRunTour] = useState(false)
  const { isOwner, isAdmin } = getGlobalState();
  const { myTreeData, setMyTreeData, handleAddToMyDashboards } = useDashboard();
  const [allTreeData, setAllTreeData] = useState(null);
  const [promoData, setPromoData] = useState(null);
  const [reccoData, setReccoData] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [nodeToDelete, setNodeToDelete] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [theme, setTheme] = useState(currentTheme);

  useEffect(() => {
    // Apply the selected theme's CSS variables
    const theme = themeConfig.themes[currentTheme];
    setTheme(theme);
    document.documentElement.style.setProperty(
      "--left-nav-bg-color",
      theme.leftNavBackgroundColor
    );
    document.documentElement.style.setProperty("--text-color", theme.textColor);
  }, [currentTheme]);

  useEffect(() => {
    if (myTreeData && allTreeData && promoData && reccoData) {
      setRunTour(true);
    }
  }, [myTreeData, allTreeData, promoData, reccoData]);

  useEffect(() => {
    const interval = setInterval(() => {
      const { myTreeData, promoData, reccoData, allTreeData } =
        getGlobalState();
      if (myTreeData) {
        setMyTreeData(myTreeData);
      }
      if (allTreeData) {
        setAllTreeData(allTreeData);
      }
      if (promoData) {
        setPromoData(promoData);
      }
      if (reccoData) {
        setReccoData(reccoData);
      }
      if (myTreeData && allTreeData && promoData && reccoData) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleAddItem = (newDashboard, parentId) => {
    newDashboard.parent_id = parentId;

    const addDashboardToTree = (tree, parentId, newDashboard) => {
      return tree.map((node) => {
        if (node.id === parentId) {
          const existingDashboard = (node.children || []).find(
            (child) => child.id === newDashboard.id
          );
          if (!existingDashboard) {
            return {
              ...node,
              children: [...(node.children || []), newDashboard],
            };
          }
        }
        if (node.children) {
          return {
            ...node,
            children: addDashboardToTree(node.children, parentId, newDashboard),
          };
        }
        return node;
      });
    };

    setMyTreeData((prevTree) => {
      const updatedTree = addDashboardToTree(prevTree, parentId, newDashboard);
      setGlobalState({ myTreeData: updatedTree });
      return updatedTree;
    });
  };

  const handleDrop = async (e, parentId) => {
    e.preventDefault();
    e.stopPropagation();

    const data = JSON.parse(e.dataTransfer.getData("application/json"));
    const { treeSource, folderId, ...newDashboard } = data;
    if (treeSource === "my-tree") {
      handleDeleteItem(newDashboard.id, folderId);
      await moveUserDashboard(parentId, newDashboard.id, folderId);
    } else {
      await addUserDashboard(parentId, newDashboard.id);
    }
    handleAddItem(newDashboard, parentId);
  };

  const handleDeleteItem = (dashboardId, folderId = "f_14") => {
    const removeDashboardFromTree = (tree, dashboardId, folderId) => {
      return tree
        .map((node) => {
          if (node.id === folderId && node.children) {
            return {
              ...node,
              children: node.children.filter(
                (child) => child.id !== dashboardId
              ),
            };
          }
          if (node.children) {
            return {
              ...node,
              children: removeDashboardFromTree(
                node.children,
                dashboardId,
                folderId
              ),
            };
          }
          return node;
        })
        .filter((node) => node.id !== dashboardId || node.id === folderId);
    };

    setMyTreeData((prevTree) => {
      const updatedTree = removeDashboardFromTree(
        prevTree,
        dashboardId,
        folderId
      );
      setGlobalState({ myTreeData: updatedTree });
      return updatedTree;
    });
  };

  // const handleAddToMyDashboards = async (dashboard) => {
  //   console.log(dashboard);
  //   const { myFolderId } = getGlobalState();
  //   dashboard.parent_id = 'f_' + myFolderId;
  //   await addUserDashboard('f_' + myFolderId, dashboard.id);
  //   handleAddItem(dashboard, 'f_' + myFolderId);
  // };

  const handleDeleteDashboard = async (dashboard) => {
    await deleteUserDashboard(
      dashboard.id.substring(2),
      dashboard.parent_id.substring(2)
    );
    handleDeleteItem(dashboard.id, dashboard.parent_id);
  };

  const handleDeleteFolder = async (nodeId) => {
    const findNode = (tree, nodeId) => {
      for (const node of tree) {
        if (node.id === nodeId) {
          return node;
        }
        if (node.children) {
          const foundNode = findNode(node.children, nodeId);
          if (foundNode) {
            return foundNode;
          }
        }
      }
      return null;
    };

    const countNode = (node) => {
      let folderCount = 0;
      let dashboardCount = 0;

      const traverse = (node) => {
        if (node.type === "folder") {
          folderCount++;
        } else if (node.type === "dashboard") {
          dashboardCount++;
        }
        if (node.children) {
          node.children.forEach((child) => traverse(child));
        }
      };

      traverse(node);
      return { folderCount, dashboardCount };
    };

    const subNode = findNode(myTreeData, nodeId);
    const { folderCount, dashboardCount } = countNode(subNode);
    setConfirmMessage(
      `Are you sure - you are about to delete ${folderCount} Folders and ${dashboardCount} Dashboards.`
    );
    setNodeToDelete(subNode);
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    const findNodeInTree = (tree, nodeId) => {
      for (const node of tree) {
        if (node.id === nodeId) {
          return node;
        }
        if (node.children) {
          const foundNode = findNodeInTree(node.children, nodeId);
          if (foundNode) {
            return foundNode;
          }
        }
      }
      return null;
    };

    const removeNodeFromTree = (tree, nodeId) => {
      return tree
        .filter((node) => node.id !== nodeId)
        .map((node) => {
          if (node.children) {
            return {
              ...node,
              children: removeNodeFromTree(node.children, nodeId),
            };
          }
          return node;
        });
    };

    const traverseSubNode = async (node) => {
      const foundNode = findNodeInTree(myTreeData, node.id);
      if (foundNode) {
        if (foundNode.type === "folder") {
          setMyTreeData((prevTree) => {
            const updatedTree = removeNodeFromTree(prevTree, foundNode.id);
            setGlobalState({ myTreeData: updatedTree });
            return updatedTree;
          });
          await deleteUserFolder(foundNode.id.substring(2));
        } else {
          setMyTreeData((prevTree) => {
            const updatedTree = removeNodeFromTree(prevTree, foundNode.id);
            setGlobalState({ myTreeData: updatedTree });
            return updatedTree;
          });
          await deleteUserDashboard(
            foundNode.id.substring(2),
            foundNode.parent_id.substring(2)
          );
        }
      }
      if (node.children) {
        for (const child of node.children) {
          await traverseSubNode(child);
        }
      }
    };

    await traverseSubNode(nodeToDelete);
    setShowConfirmModal(false);
  };

  const handleCancelDelete = () => {
    setShowConfirmModal(false);
  };

  const handleAddNewFolderClick = async (parentId, folderName) => {
    const newFolderData = await addUserFolder(
      parentId.substring(2),
      folderName
    );
    const newFolderId = "f_" + newFolderData.folder_id;
    const newFolder = {
      type: "folder",
      id: newFolderId,
      label: folderName,
      parent_id: parentId,
      url: null,
    };

    const addFolderToTree = (tree, parentId, newFolder) => {
      return tree.map((node) => {
        if (node.id === parentId) {
          return {
            ...node,
            children: [...node.children, newFolder],
          };
        }
        if (node.children) {
          return {
            ...node,
            children: addFolderToTree(node.children, parentId, newFolder),
          };
        }
        return node;
      });
    };

    setMyTreeData((prevTree) => {
      const updatedTree = addFolderToTree(prevTree, parentId, newFolder);
      setGlobalState({ myTreeData: updatedTree });
      return updatedTree;
    });
  };

  return (
      <>
        <NewUserTour run={runTour} />
        <div
            className={`left-navigation !p-0 ${isCollapsed ? "collapsed" : ""}`}
            style={{
              backgroundImage: `linear-gradient(to right, ${theme.leftNavBackgroundSecondary}, ${theme.leftNavBackgroundColor})`,
            }}
        >
          {/* Add Brand Logo In expanded sidebar */}
          {!isCollapsed && (
              <div className="py-3 flex justify-between items-center border-b border-gray-600">
                <div className="px-4 py-2 flex items-center">
            <span className="text-xl font-bold text-[#2a3042]  bg-white/50 h-[30px] w-[30px]  rounded-full text-center mr-2 flex justify-center items-center">
              N
            </span>
                  <span className="text-xl font-bold letter-spacing-2">
              NORTH STAR
            </span>
                </div>
                <button
                    className="collapse-button transition duration-300 rounded-lg hover:bg-white/10 !p-2"
                    onClick={toggleCollapse}
                >
                  <FontAwesomeIcon icon={faBars} className="fa-lg" />
                </button>
              </div>
          )}

          {isCollapsed && (
              <div>
                {/* Add brand logo in collapsable sidebar */}
                <div className="mx-3 mt-5 text-xl font-bold text-[#2a3042] p-2 bg-white h-[30px] w-[30px]  rounded-full text-center mr-2 flex justify-center items-center">
                  N
                </div>
                <div
                    className="w-[30px] !mt-2 mx-auto leftnav-icon-collapsed transition duration-300 rounded-lg hover:bg-white/10 !p-1"
                    onClick={toggleCollapse}
                >
                  <FontAwesomeIcon icon={faBars} className="fa-lg" />
                </div>
              </div>
          )}

          {!isCollapsed && (
              <div
                  className="leftnav-icon bg-white/5 hover:bg-white/10 transition duration-300 rounded-tl-2xl rounded-bl-2xl !p-3 ml-3"
                  onClick={() => setCurrentPage("default")}
              >
                <FontAwesomeIcon icon={faHome} className="pr-2" /> Home
              </div>
          )}
          {isCollapsed && (
              <div className="w-[30px] !mt-2 mx-auto leftnav-icon-collapsed transition duration-300 rounded-lg hover:bg-white/10 !p-2" onClick={toggleCollapse}>
                <FontAwesomeIcon icon={faHome} />
              </div>
          )}

          {isCollapsed ? (
              <div className="w-[30px] !mt-2 mx-auto leftnav-icon-collapsed transition duration-300 rounded-lg hover:bg-white/10 !p-2" onClick={toggleCollapse}>
                <FontAwesomeIcon icon={faFolder} />
              </div>
          ) : myTreeData ? (
              <div className="MyDashboards mt-1 bg-white/5 hover:bg-white/10 transition duration-300 rounded-tl-2xl rounded-bl-2xl !py-3 ml-3">
                <MyTreeView
                    nodes={myTreeData}
                    handleDrop={handleDrop}
                    onDeleteDashboard={handleDeleteDashboard}
                    handleAddNewFolderClick={handleAddNewFolderClick}
                    handleDeleteFolder={handleDeleteFolder}
                    setCurrentPage={setCurrentPage}
                    setSelectedNodeId={setSelectedNodeId}
                />
              </div>
          ) : (
              <div className="mt-1 px-2 !py-3 ml-3">Loading My Tree...</div>
          )}
          {isCollapsed ? (
              <div className="w-[30px] !mt-2 mx-auto leftnav-icon-collapsed transition duration-300 rounded-lg hover:bg-white/10 !p-2" onClick={toggleCollapse}>
                <FontAwesomeIcon icon={faBullhorn} />
              </div>
          ) : promoData ? (
              <div className="mt-1 bg-white/5 hover:bg-white/10 transition duration-300 rounded-tl-2xl rounded-bl-2xl !py-4 ml-3">
                <AllTreeView
                    nodes={promoData}
                    showSearchBar={false}
                    onAddToMyDashboards={handleAddToMyDashboards}
                    setCurrentPage={setCurrentPage}
                    setSelectedNodeId={setSelectedNodeId}
                />
              </div>
          ) : (
              <div className="mt-1 px-2 !py-3 ml-3">Loading Promo Data...</div>
          )}
          {isCollapsed ? (
              <div className="w-[30px] !mt-2 mx-auto leftnav-icon-collapsed transition duration-300 rounded-lg hover:bg-white/10 !p-2" onClick={toggleCollapse}>
                <FontAwesomeIcon icon={faLightbulb} />
              </div>
          ) : reccoData ? (
              <div className="mt-1 bg-white/5 hover:bg-white/10 transition duration-300 rounded-tl-2xl rounded-bl-2xl !py-3 ml-3">
                <AllTreeView
                    nodes={reccoData}
                    showSearchBar={false}
                    onAddToMyDashboards={handleAddToMyDashboards}
                    setCurrentPage={setCurrentPage}
                    setSelectedNodeId={setSelectedNodeId}
                />
              </div>
          ) : (
              <div className="mt-1 px-2 !py-3 ml-3">Loading Recco Data...</div>
          )}

          {isCollapsed ? (
              <div className="w-[30px] !mt-2 mx-auto leftnav-icon-collapsed transition duration-300 rounded-lg hover:bg-white/10 !p-2" onClick={toggleCollapse}>
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </div>
          ) : allTreeData ? (
              <div className="AllDashboards mt-1 bg-white/5 hover:bg-white/10 transition duration-300 rounded-tl-2xl rounded-bl-2xl !py-3 ml-3">
                <AllTreeView
                    nodes={allTreeData}
                    showSearchBar={true}
                    onAddToMyDashboards={handleAddToMyDashboards}
                    setCurrentPage={setCurrentPage}
                    setSelectedNodeId={setSelectedNodeId}
                />
              </div>
          ) : (
              <div className="mt-1 px-2 !py-3 ml-3">Loading All Tree...</div>
          )}

          {isCollapsed ? (
              <div className="w-[30px] !mt-2 mx-auto leftnav-icon-collapsed transition duration-300 rounded-lg hover:bg-white/10 !p-2" onClick={toggleCollapse}>
                <FontAwesomeIcon icon={faUser} />
              </div>
          ) : (
              <div className="mt-1 bg-white/5 hover:bg-white/10 transition duration-300 rounded-tl-2xl rounded-bl-2xl !py-3 ml-3">
                <div
                    className="leftnav-icon  !m-0 px-3"
                    onClick={() => setCurrentPage("profile")}
                >
                  <FontAwesomeIcon icon={faUser}  className="pr-2" /> Profile
                </div>
              </div>
          )}
          {isOwner ? (
              isCollapsed ? (
                  <div className="w-[30px] !mt-2 mx-auto leftnav-icon-collapsed transition duration-300 rounded-lg hover:bg-white/10 !p-2" onClick={toggleCollapse}>
                    <FontAwesomeIcon icon={faUserTie} />
                  </div>
              ) : (
                  <div className="mt-1 bg-white/5 hover:bg-white/10 transition duration-300 rounded-tl-2xl rounded-bl-2xl !py-3 ml-3">
                    <div
                        className="leftnav-icon  !m-0 px-3"
                        onClick={() => setCurrentPage("owner")}
                    >
                      <FontAwesomeIcon icon={faUserTie}  className="pr-2" /> Owner
                    </div>
                  </div>
              )
          ) : null}
          {isAdmin ? (
              isCollapsed ? (
                  <div className="w-[30px] !mt-2 mx-auto leftnav-icon-collapsed transition duration-300 rounded-lg hover:bg-white/10 !p-2" onClick={toggleCollapse}>
                    <FontAwesomeIcon icon={faUserShield} />
                  </div>
              ) : (
                  <div className="mt-1 bg-white/5 hover:bg-white/10 transition duration-300 rounded-tl-2xl rounded-bl-2xl !py-3 ml-3">
                    <div
                        className="leftnav-icon  !m-0 px-3"
                        onClick={() => setCurrentPage("admin")}
                    >
                      <FontAwesomeIcon icon={faUserShield}  className="pr-2" /> Admin
                    </div>
                  </div>
              )
          ) : null}
          {!isCollapsed ? (
              <div className="mt-1 bg-white/5 hover:bg-white/10 transition duration-300 rounded-tl-2xl rounded-bl-2xl !py-3 ml-3">
                <div className="leftnav-icon !m-0 px-3" onClick={logout}>
                  <FontAwesomeIcon icon={faSignOutAlt}  className="pr-2" /> Logout
                </div>
              </div>
          ) : (
              <div className="w-[30px] !mt-2 mx-auto leftnav-icon-collapsed transition duration-300 rounded-lg hover:bg-white/10 !p-2 " onClick={toggleCollapse}>
                <FontAwesomeIcon icon={faSignOutAlt} />
              </div>
          )}

          {showConfirmModal && (
              <ConfirmModal
                  message={confirmMessage}
                  onConfirm={handleConfirmDelete}
                  onCancel={handleCancelDelete}
              />
          )}
        </div>
      </>

  );
};

export default LeftNavigation;
